import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Camera, Loader2, X, ExternalLink, CheckCircle2, Clock, LogIn, LogOut } from "lucide-react";
import api, { apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export function EventDialog({ open, onOpenChange, mode = "checkin", entityType, entityId, company, event, onSaved }) {
  const [geo, setGeo] = useState({ status: "idle" });
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [now, setNow] = useState(new Date());
  const isCheckout = mode === "checkout";

  useEffect(() => {
    if (!open) return;
    setAddress(""); setNotes(""); setPhotos([]); setUploading("");
    setGeo({ status: "capturing" });
    if (!navigator.geolocation) { setGeo({ status: "unavailable" }); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setGeo({ status: "captured", latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => setGeo({ status: "unavailable" }),
      { enableHighAccuracy: true, timeout: 10000 }
    );
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, [open]);

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotos((p) => [...p, ...files.map((f) => ({ file: f, url: URL.createObjectURL(f) }))]);
    e.target.value = "";
  };
  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const save = async () => {
    if (isCheckout && !notes.trim()) {
      toast.error("Please enter your meeting / site-visit comments before checking out");
      return;
    }
    setSaving(true);
    try {
      const loc = { latitude: geo.latitude ?? null, longitude: geo.longitude ?? null, accuracy: geo.accuracy ?? null };
      let visitId;
      if (isCheckout) {
        const { data } = await api.post(`/visits/${event.id}/check-out`, { ...loc, notes });
        visitId = data.id;
      } else {
        const { data } = await api.post("/visits/check-in", {
          entity_type: entityType, entity_id: entityId, ...loc, address, notes,
        });
        visitId = data.id;
      }
      for (let i = 0; i < photos.length; i++) {
        setUploading(`${i + 1}/${photos.length}`);
        const fd = new FormData();
        fd.append("file", photos[i].file, photos[i].file.name || `photo${i}.jpg`);
        await api.post(`/visits/${visitId}/photo`, fd);
      }
      toast.success(isCheckout ? "Checked out — event completed" : "Checked in — event started");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
      setUploading("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {isCheckout ? <LogOut className="h-4 w-4 text-brand" /> : <LogIn className="h-4 w-4 text-brand" />}
            {isCheckout ? "Check Out" : "Check In — New Event"}
          </DialogTitle>
          <DialogDescription>
            {isCheckout
              ? `Ending your visit at ${event?.entity_name || "site"}. Location & time are captured automatically.`
              : `${company ? `For ${company}. ` : ""}Location & time are captured automatically.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border px-3 py-2.5 text-sm flex items-center justify-between" data-testid="event-time">
            <span className="flex items-center gap-2 text-slate-600"><Clock className="h-4 w-4 text-brand" /> {now.toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
            <span className="text-[10px] text-slate-400 uppercase tracking-wider">{isCheckout ? "Check-out time" : "Check-in time"}</span>
          </div>

          <div className="rounded-md border px-3 py-2.5 text-sm" data-testid="geo-status">
            {geo.status === "capturing" && <span className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Capturing GPS location…</span>}
            {geo.status === "captured" && (
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-4 w-4" /> {geo.latitude.toFixed(5)}, {geo.longitude.toFixed(5)} <span className="text-slate-400 text-xs">±{Math.round(geo.accuracy)}m</span></span>
                <a href={`https://www.google.com/maps?q=${geo.latitude},${geo.longitude}`} target="_blank" rel="noreferrer" className="text-brand text-xs flex items-center gap-1 hover:underline" data-testid="geo-map-link">Map <ExternalLink className="h-3 w-3" /></a>
              </div>
            )}
            {geo.status === "unavailable" && <span className="text-amber-600">Location unavailable — event will be saved without GPS.</span>}
          </div>

          {!isCheckout && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-700">Site Address</Label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)} className="h-9" placeholder="Type the site address" data-testid="event-address" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">{isCheckout ? <>Closing Notes / Meeting Comments <span className="text-red-500">*</span></> : "Notes"}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder={isCheckout ? "What was discussed, outcomes, next steps… (required)" : "Measurements, observations, next steps…"} data-testid="event-notes" />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Photos</Label>
            <div className="flex flex-wrap gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative h-16 w-16 rounded-md overflow-hidden border" data-testid={`event-photo-preview-${i}`}>
                  <img src={p.url} alt="" className="h-full w-full object-cover" />
                  <button onClick={() => removePhoto(i)} className="absolute top-0 right-0 bg-black/60 rounded-bl p-0.5" data-testid={`remove-photo-${i}`}><X className="h-3 w-3 text-white" /></button>
                </div>
              ))}
              <label className="h-16 w-16 rounded-md border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-brand text-slate-400 hover:text-brand" data-testid="add-photo-btn">
                <Camera className="h-5 w-5" />
                <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={onPick} data-testid="event-photo-input" />
              </label>
            </div>
            <p className="text-[10px] text-slate-400">Gallery upload or camera — on mobile the camera opens directly.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="event-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand/90 text-white gap-2" data-testid={isCheckout ? "event-checkout-submit" : "event-checkin-submit"}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {uploading ? `Uploading ${uploading}…` : isCheckout ? "Check Out" : "Check In"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
